/**
 * React Query hooks for the Charity Verify screen.
 *
 * Two list queries (pending queue + verified) and two mutations
 * (verify / unverify). Each mutation invalidates BOTH list queries on
 * success so the tab counts and rows stay in sync after an app moves
 * between pending and verified without a manual refetch — mirrors the
 * legacy dashboard hook's invalidation behavior.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UnverifyCharityInput,
  VerifyCharityInput,
  fetchCharityQueue,
  fetchCharityVerified,
  unverifyCharity,
  verifyCharity
} from '@/api/charityVerify';

export function useCharityQueue(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['charityQueue'],
    queryFn: fetchCharityQueue,
    enabled: options?.enabled
  });
}

export function useCharityVerified(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['charityVerified'],
    queryFn: fetchCharityVerified,
    enabled: options?.enabled
  });
}

export function useVerifyCharity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VerifyCharityInput) => verifyCharity(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['charityQueue'] });
      qc.invalidateQueries({ queryKey: ['charityVerified'] });
    }
  });
}

export function useUnverifyCharity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UnverifyCharityInput) => unverifyCharity(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['charityQueue'] });
      qc.invalidateQueries({ queryKey: ['charityVerified'] });
    }
  });
}
